/**
 * ConfirmDialogProvider — native confirm() 대체용 디자인된 모달 Provider.
 *
 * 사용:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "배합을 삭제할까요?",         // 질문형
 *     summary: "'C-25' 배합",              // 대상·수치 한 줄
 *     effects: ["배합 목록과 견적 선택지에서 빠집니다."],
 *     irreversible: true,
 *     kind: "danger",
 *     confirmLabel: "삭제",
 *   });
 *   if (!ok) return;
 *
 * 모양·키보드는 components/ui/ConfirmPanel.tsx (D안: kind 색 제목 + 영향 목록).
 *   - kind 기준: danger=삭제·영구 제거 / warning=덮어쓰기·적용·발송·버리기 / info=일반 진행 (confirm-context.ts)
 *   - danger 는 취소 버튼에 첫 포커스(실수로 Enter 를 눌러도 실행되지 않게), 그 밖에는 확인 버튼.
 *   - Enter 는 포커스된 버튼만 누른다(전역 Enter 확정 없음). Esc·배경 클릭 = 취소.
 */
import { useCallback, useRef, useState, type ReactNode } from "react";
import { ConfirmPanel } from "@/components/ui/ConfirmPanel";
import { ConfirmContext, type ConfirmKind, type ConfirmOptions } from "./confirm-context";

interface ActiveConfirm {
  id: number;
  opts: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

let _idSeq = 1;

const DEFAULT_TITLE: Record<ConfirmKind, string> = { info: "계속할까요?", warning: "계속할까요?", danger: "삭제할까요?" };

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveConfirm | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setActive({ id: _idSeq++, opts, resolve });
    });
  }, []);

  function close(ok: boolean) {
    if (!active) return;
    active.resolve(ok);
    setActive(null);
  }

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {active && <ConfirmDialog key={active.id} opts={active.opts} onClose={close} />}
    </ConfirmContext.Provider>
  );
}

function ConfirmDialog({ opts, onClose }: { opts: ConfirmOptions; onClose: (ok: boolean) => void }) {
  const kind: ConfirmKind = opts.kind ?? "info";
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const btnClass = kind === "info" ? "btn-primary" : `btn-primary confirm-btn-${kind}`;
  return (
    <ConfirmPanel
      kind={kind}
      title={opts.title ?? DEFAULT_TITLE[kind]}
      summary={opts.summary}
      effects={opts.effects}
      irreversible={opts.irreversible}
      onDismiss={() => onClose(false)}
      initialFocus={kind === "danger" ? cancelRef : confirmRef}
      actions={<>
        <button ref={cancelRef} type="button" className="btn-ghost" onClick={() => onClose(false)}>{opts.cancelLabel ?? "취소"}</button>
        <button ref={confirmRef} type="button" className={btnClass} onClick={() => onClose(true)}>{opts.confirmLabel ?? "확인"}</button>
      </>}
    >
      {opts.body != null && <div className="confirm-extra">{opts.body}</div>}
    </ConfirmPanel>
  );
}
